import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  ExternalLink,
  Inbox,
  Loader2,
  MapPin,
  MessageSquareWarning,
  RefreshCw,
  Search,
  ShieldAlert,
  UserCheck,
  UsersRound,
  X,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import {
  assignLeadToPartner,
  fetchOperations,
  fetchPartnerSuggestions,
  updateOperationalAlert,
  type OperationsAlert,
  type OperationsCategory,
  type OperationsSeverity,
  type OperationsStatus,
  type OperationsSummary,
  type PartnerSuggestion,
} from "@/api/adminOperations";
import { AdminErrorState } from "@/components/admin/AdminDataState";
import { PageHeader } from "@/components/layouts/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

const formatNumber = new Intl.NumberFormat("el-GR").format;

const EMPTY_SUMMARY: OperationsSummary = {
  active: 0,
  open: 0,
  acknowledged: 0,
  critical: 0,
  oldest_open_at: null,
  types: {},
};

const SERVICE_LABELS: Record<string, string> = {
  εξοικονομώ: "Εξοικονομώ",
  αντλίεςΘερμότητας: "Αντλίες θερμότητας",
  φωτοβολταϊκά: "Φωτοβολταϊκά",
  κλιματιστικά: "Κλιματιστικά",
  φυσικόΑέριο: "Φυσικό αέριο",
  ηλιακά: "Ηλιακά",
  ανακαινίζωΕνεργειακά: "Ανακαινίζω - Ενεργειακά συστήματα",
  ανακαινίζωΠλήρηςΑνακαίνιση: "Ανακαινίζω - Πλήρης ανακαίνιση",
  άλλο: "Άλλο",
};

const TYPE_META: Record<string, { label: string; description: string; icon: LucideIcon }> = {
  unassigned_lead: {
    label: "Χωρίς συνεργάτη",
    description: "Δεν βρέθηκε αυτόματη κάλυψη για το lead.",
    icon: UserCheck,
  },
  lead_sla: {
    label: "Καθυστέρηση σταδίου",
    description: "Δεν έχει καταγραφεί εξέλιξη για περισσότερες από 48 ώρες.",
    icon: Clock3,
  },
  notification_failed: {
    label: "Αποτυχία ειδοποίησης",
    description: "Ολοκληρώθηκαν τα retries χωρίς επιτυχή αποστολή.",
    icon: MessageSquareWarning,
  },
  notification_overdue: {
    label: "Εκπρόθεσμη ειδοποίηση",
    description: "Η προγραμματισμένη αποστολή δεν εκτελέστηκε εγκαίρως.",
    icon: BellRing,
  },
  meta_failed: {
    label: "Αποτυχία Meta event",
    description: "Το realtime event δεν ολοκλήρωσε την επεξεργασία του.",
    icon: Zap,
  },
  meta_retry: {
    label: "Meta event σε retry",
    description: "Η πλατφόρμα θα επαναλάβει αυτόματα την επεξεργασία.",
    icon: RefreshCw,
  },
  meta_unmapped: {
    label: "Meta event χωρίς mapping",
    description: "Η φόρμα δεν αντιστοιχίστηκε σε ενεργή διαδρομή.",
    icon: Zap,
  },
};

const DETAIL_LABELS: Record<string, string> = {
  no_partner_in_coverage: "Δεν βρέθηκε συνεργάτης εντός κάλυψης",
  stage_sla_48h: "Χωρίς αλλαγή σταδίου για 48+ ώρες",
};

const CATEGORY_OPTIONS: Array<{
  value: OperationsCategory;
  label: string;
  helper: string;
  icon: LucideIcon;
  tone: string;
}> = [
  { value: "unassigned", label: "Χωρίς συνεργάτη", helper: "Χειροκίνητη ανάθεση", icon: UsersRound, tone: "text-amber-700 bg-amber-50 ring-amber-100" },
  { value: "sla", label: "SLA 48 ωρών", helper: "Αδράνεια σταδίου", icon: Clock3, tone: "text-orange-700 bg-orange-50 ring-orange-100" },
  { value: "notifications", label: "Ειδοποιήσεις", helper: "Email & SMS", icon: BellRing, tone: "text-rose-700 bg-rose-50 ring-rose-100" },
  { value: "meta", label: "Meta intake", helper: "Retry, failed, unmapped", icon: Zap, tone: "text-sky-700 bg-sky-50 ring-sky-100" },
];

const parseApiDate = (value?: string | null): Date | null => {
  if (!value) return null;
  try {
    const parsed = parseISO(value.replace(" ", "T"));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const relativeTime = (value?: string | null): string => {
  const parsed = parseApiDate(value);
  return parsed ? formatDistanceToNow(parsed, { addSuffix: true, locale: el }) : "άγνωστος χρόνος";
};

const absoluteTime = (value?: string | null): string => {
  const parsed = parseApiDate(value);
  return parsed ? format(parsed, "d MMM yyyy, HH:mm", { locale: el }) : "—";
};

const categoryCount = (summary: OperationsSummary, category: OperationsCategory): number => {
  if (category === "unassigned") return Number(summary.types.unassigned_lead || 0);
  if (category === "sla") return Number(summary.types.lead_sla || 0);
  if (category === "notifications") {
    return Number(summary.types.notification_failed || 0) + Number(summary.types.notification_overdue || 0);
  }
  if (category === "meta") {
    return Number(summary.types.meta_failed || 0)
      + Number(summary.types.meta_retry || 0)
      + Number(summary.types.meta_unmapped || 0);
  }
  return summary.active;
};

function OperationsSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-52 w-full rounded-3xl" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28 rounded-2xl" />)}
      </div>
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-44 w-full rounded-2xl" />)}
      </div>
    </div>
  );
}

function AlertRow({
  alert,
  updating,
  onAcknowledge,
  onAssign,
  onResolve,
}: {
  alert: OperationsAlert;
  updating: boolean;
  onAcknowledge: () => void;
  onAssign: () => void;
  onResolve: () => void;
}) {
  const meta = TYPE_META[alert.alert_type] || {
    label: alert.title,
    description: "Operational εξαίρεση που χρειάζεται έλεγχο.",
    icon: AlertTriangle,
  };
  const Icon = meta.icon;
  const critical = alert.severity === "critical";
  const acknowledged = alert.status === "acknowledged";
  const service = alert.lead_service ? SERVICE_LABELS[alert.lead_service] || alert.lead_service : null;

  return (
    <article
      className={cn(
        "admin-panel relative overflow-hidden transition-colors hover:border-slate-300",
        critical && "border-rose-200",
      )}
    >
      <div className={cn("absolute inset-y-0 left-0 w-1", critical ? "bg-rose-500" : acknowledged ? "bg-sky-400" : "bg-amber-400")} />
      <div className="p-4 pl-5 sm:p-5 sm:pl-6">
        <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex min-w-0 gap-3.5">
            <div className={cn(
              "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1",
              critical ? "bg-rose-50 text-rose-700 ring-rose-100" : "bg-amber-50 text-amber-700 ring-amber-100",
            )}>
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-semibold leading-6 text-slate-950">{meta.label}</h2>
                {critical && <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50">Κρίσιμο</Badge>}
                <Badge variant="outline" className={acknowledged ? "border-sky-200 bg-sky-50 text-sky-700" : "border-amber-200 bg-amber-50 text-amber-700"}>
                  {acknowledged ? "Σε χειρισμό" : "Χωρίς ανάληψη"}
                </Badge>
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-500">{meta.description}</p>

              {alert.lead_id && (
                <div className="mt-3 rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-100">
                  <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                    <Link
                      to={`/admin/lead/${encodeURIComponent(alert.lead_id)}`}
                      className="min-w-0 truncate text-sm font-semibold text-slate-900 underline-offset-4 hover:text-sky-700 hover:underline"
                    >
                      {alert.lead_name || "Lead χωρίς όνομα"}
                    </Link>
                    {service && <span className="shrink-0 text-xs text-slate-500 sm:before:mr-2 sm:before:content-['·']">{service}</span>}
                  </div>
                  {alert.lead_address && (
                    <p className="mt-1 flex min-w-0 items-start gap-1.5 text-xs leading-5 text-slate-500">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 break-words">{alert.lead_address}</span>
                    </p>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                <span title={absoluteTime(alert.created_at)}>{relativeTime(alert.created_at)}</span>
                {alert.detail_code && (
                  <span className="min-w-0 break-all font-medium text-slate-500">
                    {DETAIL_LABELS[alert.detail_code] || alert.detail_code}
                  </span>
                )}
                {acknowledged && alert.acknowledged_at && <span>Αναλήφθηκε {relativeTime(alert.acknowledged_at)}</span>}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-slate-100 pt-4 xl:max-w-[390px] xl:justify-end xl:border-0 xl:pt-0">
            {alert.alert_type === "unassigned_lead" && alert.lead_id && (
              <Button className="min-h-11 flex-1 bg-slate-950 hover:bg-slate-800 sm:flex-none" onClick={onAssign} disabled={updating}>
                <UserCheck className="mr-2 h-4 w-4" /> Ανάθεση
              </Button>
            )}
            {alert.status === "open" && (
              <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" onClick={onAcknowledge} disabled={updating}>
                {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CircleDot className="mr-2 h-4 w-4" />}
                Ανάληψη
              </Button>
            )}
            {alert.lead_id && (
              <Button asChild variant="ghost" className="min-h-11 flex-1 sm:flex-none">
                <Link to={`/admin/lead/${encodeURIComponent(alert.lead_id)}`}>
                  Άνοιγμα <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
            <Button variant="ghost" className="min-h-11 flex-1 text-slate-500 hover:text-slate-900 sm:flex-none" onClick={onResolve} disabled={updating}>
              Κλείσιμο
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

const OperationsInbox = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OperationsStatus>("active");
  const [severity, setSeverity] = useState<OperationsSeverity>("all");
  const [category, setCategory] = useState<OperationsCategory>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assigningAlert, setAssigningAlert] = useState<OperationsAlert | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [resolvingAlert, setResolvingAlert] = useState<OperationsAlert | null>(null);
  const [forceRefreshing, setForceRefreshing] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const operationsQueryKey = ["admin-operations", page, status, severity, category, debouncedSearch] as const;
  const operationsQuery = useQuery({
    queryKey: operationsQueryKey,
    queryFn: () => fetchOperations({ page, perPage: 25, status, severity, category, search: debouncedSearch }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const summary = operationsQuery.data?.summary || EMPTY_SUMMARY;
  const pagination = operationsQuery.data?.pagination || { page, per_page: 25, total: 0, pages: 1 };
  const alerts = operationsQuery.data?.alerts || [];
  const activeOwnershipRate = summary.active > 0 ? Math.round((summary.acknowledged / summary.active) * 100) : 100;

  useEffect(() => {
    if (page > pagination.pages) setPage(Math.max(1, pagination.pages));
  }, [page, pagination.pages]);

  const updateMutation = useMutation({
    mutationFn: ({ alertId, action }: { alertId: number; action: "acknowledge" | "resolve" }) =>
      updateOperationalAlert(alertId, action),
    onSuccess: async (_, variables) => {
      toast({
        title: variables.action === "acknowledge" ? "Η εξαίρεση αναλήφθηκε" : "Η εξαίρεση έκλεισε",
        description: variables.action === "resolve"
          ? "Αν η αιτία παραμένει ενεργή, ο επόμενος έλεγχος θα την ανοίξει ξανά."
          : "Εμφανίζεται πλέον ως υπό διαχείριση.",
      });
      setResolvingAlert(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-operations"] });
    },
    onError: (error) => toast({
      title: "Η ενέργεια δεν ολοκληρώθηκε",
      description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
      variant: "destructive",
    }),
  });

  const suggestionsQuery = useQuery({
    queryKey: ["admin-operation-suggestions", assigningAlert?.lead_id],
    queryFn: () => fetchPartnerSuggestions(assigningAlert?.lead_id || ""),
    enabled: Boolean(assigningAlert?.lead_id),
    staleTime: 30_000,
  });

  const assignmentMutation = useMutation({
    mutationFn: ({ leadId, partnerId }: { leadId: string; partnerId: string }) => assignLeadToPartner(leadId, partnerId),
    onSuccess: async () => {
      toast({ title: "Η ανάθεση ολοκληρώθηκε", description: "Email και SMS δημιουργήθηκαν idempotently στην ουρά." });
      setAssigningAlert(null);
      setSelectedPartnerId("");
      setPartnerSearch("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-operations"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-lead-metrics"] }),
      ]);
    },
    onError: (error) => toast({
      title: "Η ανάθεση δεν ολοκληρώθηκε",
      description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
      variant: "destructive",
    }),
  });

  const filteredSuggestions = useMemo(() => {
    const normalized = partnerSearch.trim().toLocaleLowerCase("el-GR");
    const rows = suggestionsQuery.data || [];
    if (!normalized) return rows;
    return rows.filter((partner) => `${partner.name} ${partner.location}`.toLocaleLowerCase("el-GR").includes(normalized));
  }, [partnerSearch, suggestionsQuery.data]);

  const selectedPartner: PartnerSuggestion | undefined = (suggestionsQuery.data || [])
    .find((partner) => partner.id === selectedPartnerId);

  const hasFilters = status !== "active" || severity !== "all" || category !== "all" || search.trim() !== "";
  const resetFilters = () => {
    setStatus("active");
    setSeverity("all");
    setCategory("all");
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  const selectCategory = (value: OperationsCategory) => {
    setCategory((current) => current === value ? "all" : value);
    setPage(1);
  };

  const openAssignment = (alert: OperationsAlert) => {
    setAssigningAlert(alert);
    setSelectedPartnerId("");
    setPartnerSearch("");
  };

  const forceRefresh = async () => {
    setForceRefreshing(true);
    try {
      const data = await fetchOperations(
        { page, perPage: 25, status, severity, category, search: debouncedSearch },
        true,
      );
      queryClient.setQueryData(operationsQueryKey, data);
      toast({ title: "Η ουρά ενημερώθηκε", description: "Ο server επανέλεγξε leads, SLA, Meta events και ειδοποιήσεις." });
    } catch (error) {
      toast({
        title: "Η ανανέωση δεν ολοκληρώθηκε",
        description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setForceRefreshing(false);
    }
  };

  const closeAssignment = () => {
    if (assignmentMutation.isPending) return;
    setAssigningAlert(null);
    setSelectedPartnerId("");
    setPartnerSearch("");
  };

  return (
    <div className="space-y-6 sm:space-y-7">
      <PageHeader
        eyebrow="Καθημερινή λειτουργία"
        title="Operations Inbox"
        description="Μία ελεγχόμενη ουρά για leads χωρίς συνεργάτη, καθυστερήσεις SLA και αποτυχίες Meta ή ειδοποιήσεων."
        icon={<ShieldAlert />}
        iconGradient="orange"
        actions={(
          <>
            <Button asChild variant="outline" className="min-h-11 flex-1 sm:flex-none">
              <Link to="/admin/leads"><Inbox className="mr-2 h-4 w-4" /> Όλα τα leads</Link>
            </Button>
            <Button
              variant="outline"
              className="min-h-11 flex-1 sm:flex-none"
              onClick={() => void forceRefresh()}
              disabled={forceRefreshing}
            >
              <RefreshCw className={cn("mr-2 h-4 w-4", forceRefreshing && "animate-spin")} /> Ανανέωση
            </Button>
          </>
        )}
      />

      {operationsQuery.isLoading ? (
        <OperationsSkeleton />
      ) : operationsQuery.isError ? (
        <AdminErrorState
          message={operationsQuery.error instanceof Error ? operationsQuery.error.message : "Το Operations Inbox δεν είναι διαθέσιμο."}
          onRetry={() => void operationsQuery.refetch()}
        />
      ) : (
        <>
          <section className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-[0_20px_55px_-35px_rgba(15,23,42,0.8)]">
            <div className="grid min-w-0 gap-8 px-5 py-6 sm:px-7 sm:py-7 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:items-end lg:px-8 lg:py-8">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200 ring-1 ring-inset ring-white/10">
                    <CircleDot className="h-3.5 w-3.5 text-amber-300" /> Ουρά χωρίς ανάληψη
                  </span>
                  {summary.critical > 0 && (
                    <span className="rounded-full bg-rose-400/15 px-3 py-1 text-xs font-semibold text-rose-200 ring-1 ring-inset ring-rose-300/20">
                      {formatNumber(summary.critical)} κρίσιμα
                    </span>
                  )}
                </div>
                <div className="mt-5 flex items-end gap-3">
                  <p className="text-5xl font-bold tabular-nums tracking-[-0.06em] sm:text-6xl">{formatNumber(summary.open)}</p>
                  <p className="max-w-48 pb-1 text-sm leading-5 text-slate-300">εξαιρέσεις περιμένουν ιδιοκτήτη και επόμενο βήμα</p>
                </div>
                <p className="mt-5 max-w-2xl text-sm leading-6 text-slate-300">
                  {summary.oldest_open_at
                    ? `Η παλαιότερη ανοιχτή εξαίρεση δημιουργήθηκε ${relativeTime(summary.oldest_open_at)}.`
                    : "Δεν υπάρχει ανοιχτή εξαίρεση χωρίς ανάληψη."}
                </p>
              </div>

              <div className="grid min-w-0 grid-cols-2 gap-px overflow-hidden rounded-2xl bg-white/10 ring-1 ring-white/10">
                <div className="min-w-0 bg-white/[0.04] p-4 sm:p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Ενεργά</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight">{formatNumber(summary.active)}</p>
                  <p className="mt-1 text-xs text-slate-400">open + σε χειρισμό</p>
                </div>
                <div className="min-w-0 bg-white/[0.04] p-4 sm:p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Σε χειρισμό</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight">{formatNumber(summary.acknowledged)}</p>
                  <p className="mt-1 text-xs text-slate-400">{activeOwnershipRate}% της ουράς</p>
                </div>
                <div className="col-span-2 bg-white/[0.04] p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-300">
                    <span>Κάλυψη ανάληψης</span><span className="font-semibold tabular-nums text-white">{activeOwnershipRate}%</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-sky-400 transition-[width] duration-300" style={{ width: `${activeOwnershipRate}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section aria-label="Κατηγορίες εξαιρέσεων" className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
            {CATEGORY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const selected = category === option.value;
              const count = categoryCount(summary, option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectCategory(option.value)}
                  aria-pressed={selected}
                  className={cn(
                    "admin-panel min-w-0 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 sm:p-5",
                    selected && "border-slate-950 ring-2 ring-slate-950 ring-offset-2",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1", option.tone)}><Icon className="h-4 w-4" /></span>
                    <span className="text-2xl font-bold tabular-nums tracking-tight text-slate-950">{formatNumber(count)}</span>
                  </div>
                  <p className="mt-4 text-sm font-semibold text-slate-900">{option.label}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{option.helper}</p>
                </button>
              );
            })}
          </section>

          <section className="admin-filter-bar" aria-label="Φίλτρα Operations Inbox">
            <div className="relative min-w-0 flex-1 sm:min-w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Όνομα, περιοχή, υπηρεσία ή κωδικός…"
                className="h-11 border-slate-200 bg-slate-50 pl-9 pr-10"
                aria-label="Αναζήτηση εξαιρέσεων"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  aria-label="Καθαρισμός αναζήτησης"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={status} onValueChange={(value: OperationsStatus) => { setStatus(value); setPage(1); }}>
              <SelectTrigger className="h-11 w-full border-slate-200 bg-white sm:w-[170px]" aria-label="Κατάσταση εξαίρεσης"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Όλα τα ενεργά</SelectItem>
                <SelectItem value="open">Χωρίς ανάληψη</SelectItem>
                <SelectItem value="acknowledged">Σε χειρισμό</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severity} onValueChange={(value: OperationsSeverity) => { setSeverity(value); setPage(1); }}>
              <SelectTrigger className="h-11 w-full border-slate-200 bg-white sm:w-[160px]" aria-label="Σοβαρότητα εξαίρεσης"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Κάθε σοβαρότητα</SelectItem>
                <SelectItem value="critical">Κρίσιμα</SelectItem>
                <SelectItem value="warning">Προειδοποιήσεις</SelectItem>
                <SelectItem value="info">Πληροφορίες</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" className="min-h-11 text-slate-500" onClick={resetFilters}>
                <X className="mr-2 h-4 w-4" /> Καθαρισμός
              </Button>
            )}
          </section>

          <section className="space-y-3" aria-label="Ενεργές εξαιρέσεις">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-[-0.015em] text-slate-950">Ουρά ενεργειών</h2>
                <p className="mt-0.5 text-sm leading-6 text-slate-500">
                  {formatNumber(pagination.total)} {pagination.total === 1 ? "αποτέλεσμα" : "αποτελέσματα"} με τα τρέχοντα φίλτρα.
                </p>
              </div>
              {operationsQuery.isFetching && !operationsQuery.isLoading && (
                <span className="inline-flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Ενημέρωση…</span>
              )}
            </div>

            {alerts.length === 0 ? (
              <div className="admin-panel flex min-h-64 flex-col items-center justify-center px-6 py-12 text-center">
                <div className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-2xl ring-1",
                  summary.active === 0 ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-slate-100 text-slate-500 ring-slate-200",
                )}>
                  {summary.active === 0 ? <CheckCircle2 className="h-5 w-5" /> : <Search className="h-5 w-5" />}
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">
                  {summary.active === 0 ? "Η ουρά είναι καθαρή" : "Δεν βρέθηκαν εξαιρέσεις"}
                </h3>
                <p className="mt-1 max-w-md text-sm leading-6 text-slate-500">
                  {summary.active === 0
                    ? "Δεν υπάρχει ενεργό operational θέμα που να απαιτεί ανθρώπινη ενέργεια."
                    : "Αλλάξτε ή καθαρίστε τα φίλτρα για να δείτε την υπόλοιπη ενεργή ουρά."}
                </p>
                {hasFilters && <Button variant="outline" className="mt-5 min-h-11" onClick={resetFilters}>Καθαρισμός φίλτρων</Button>}
              </div>
            ) : (
              alerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  updating={updateMutation.isPending && updateMutation.variables?.alertId === alert.id}
                  onAcknowledge={() => updateMutation.mutate({ alertId: alert.id, action: "acknowledge" })}
                  onAssign={() => openAssignment(alert)}
                  onResolve={() => setResolvingAlert(alert)}
                />
              ))
            )}

            {pagination.pages > 1 && (
              <div className="admin-panel flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                <p className="text-center text-xs text-slate-500 sm:text-left">
                  Σελίδα <span className="font-semibold text-slate-800">{pagination.page}</span> από {pagination.pages} · {formatNumber(pagination.total)} συνολικά
                </p>
                <div className="grid grid-cols-2 gap-2 sm:flex">
                  <Button variant="outline" className="min-h-11" disabled={page <= 1 || operationsQuery.isFetching} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                    <ChevronLeft className="mr-1 h-4 w-4" /> Προηγούμενη
                  </Button>
                  <Button variant="outline" className="min-h-11" disabled={page >= pagination.pages || operationsQuery.isFetching} onClick={() => setPage((current) => current + 1)}>
                    Επόμενη <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <Dialog open={Boolean(assigningAlert)} onOpenChange={(open) => !open && closeAssignment()}>
        <DialogContent className="max-h-[92dvh] overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="border-b border-slate-100 px-5 pb-4 pt-5 text-left sm:px-6 sm:pt-6">
            <DialogTitle>Χειροκίνητη ανάθεση</DialogTitle>
            <DialogDescription>
              Επιλέξτε συνειδητά συνεργάτη. Η λίστα είναι ταξινομημένη ανά απόσταση και δεν γίνεται αυτόματη ανάθεση εκτός ακτίνας.
            </DialogDescription>
            {assigningAlert && (
              <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm ring-1 ring-inset ring-slate-100">
                <p className="font-semibold text-slate-900">{assigningAlert.lead_name || "Lead χωρίς όνομα"}</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {[assigningAlert.lead_service ? SERVICE_LABELS[assigningAlert.lead_service] || assigningAlert.lead_service : null, assigningAlert.lead_address].filter(Boolean).join(" · ")}
                </p>
              </div>
            )}
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto px-5 py-4 sm:px-6">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={partnerSearch} onChange={(event) => setPartnerSearch(event.target.value)} placeholder="Αναζήτηση συνεργάτη ή περιοχής…" className="h-11 pl-9" />
            </div>

            <div className="mt-4 space-y-2">
              {suggestionsQuery.isLoading ? (
                Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-20 rounded-xl" />)
              ) : suggestionsQuery.isError ? (
                <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
                  Δεν φορτώθηκαν οι κατάλληλοι συνεργάτες. Δοκιμάστε ξανά.
                </div>
              ) : filteredSuggestions.length === 0 ? (
                <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500 ring-1 ring-inset ring-slate-100">
                  Δεν βρέθηκε ενεργός συνεργάτης για αυτή την υπηρεσία και αναζήτηση.
                </div>
              ) : filteredSuggestions.map((partner) => {
                const selected = partner.id === selectedPartnerId;
                return (
                  <button
                    key={partner.id}
                    type="button"
                    onClick={() => setSelectedPartnerId(partner.id)}
                    aria-pressed={selected}
                    className={cn(
                      "flex min-h-20 w-full min-w-0 items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2",
                      selected ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">{partner.name}</span>
                      <span className={cn("mt-1 block truncate text-xs", selected ? "text-slate-300" : "text-slate-500")}>{partner.location || "Χωρίς καταχωρημένη περιοχή"}</span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums">{partner.distance_km === null ? "—" : `${partner.distance_km} km`}</span>
                      <span className={cn(
                        "mt-1 block text-[11px] font-medium",
                        selected ? partner.within_radius ? "text-emerald-300" : "text-amber-300" : partner.within_radius ? "text-emerald-700" : "text-amber-700",
                      )}>
                        {partner.distance_km === null ? "Άγνωστη απόσταση" : partner.within_radius ? `Εντός ακτίνας ${partner.radius_km} km` : `Εκτός ακτίνας ${partner.radius_km} km`}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter className="border-t border-slate-100 bg-slate-50 px-5 py-4 sm:items-center sm:px-6">
            <div className="min-w-0 flex-1 text-left">
              {selectedPartner && (
                <p className={cn("text-xs leading-5", selectedPartner.within_radius ? "text-emerald-700" : "text-amber-700")}>
                  {selectedPartner.within_radius
                    ? "Ο συνεργάτης βρίσκεται εντός της δηλωμένης ακτίνας κάλυψης."
                    : "Προσοχή: πρόκειται για συνειδητή χειροκίνητη ανάθεση εκτός ακτίνας."}
                </p>
              )}
            </div>
            <Button variant="outline" className="min-h-11" onClick={closeAssignment} disabled={assignmentMutation.isPending}>Ακύρωση</Button>
            <Button
              className="min-h-11 bg-slate-950 hover:bg-slate-800"
              disabled={!assigningAlert?.lead_id || !selectedPartnerId || assignmentMutation.isPending}
              onClick={() => {
                if (assigningAlert?.lead_id && selectedPartnerId) {
                  assignmentMutation.mutate({ leadId: assigningAlert.lead_id, partnerId: selectedPartnerId });
                }
              }}
            >
              {assignmentMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {selectedPartner && !selectedPartner.within_radius ? "Ανάθεση εκτός ακτίνας" : "Ανάθεση και ουρά"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(resolvingAlert)} onOpenChange={(open) => !open && setResolvingAlert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Κλείσιμο operational εξαίρεσης;</AlertDialogTitle>
            <AlertDialogDescription>
              Το κλείσιμο είναι χειροκίνητο. Αν η πραγματική αιτία παραμένει — για παράδειγμα το lead συνεχίζει χωρίς συνεργάτη — ο επόμενος server έλεγχος θα ανοίξει ξανά το alert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isPending}>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction
              disabled={!resolvingAlert || updateMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (resolvingAlert) updateMutation.mutate({ alertId: resolvingAlert.id, action: "resolve" });
              }}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Κλείσιμο εξαίρεσης
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperationsInbox;
